/**
 * Ponto-no-polígono rápido para as áreas do filtro dos "Últimos focos"
 * (municípios, estados, sub-regiões, TIs, UCs). Função pura, sem OpenLayers
 * (testada em areaShape.test.ts).
 *
 * O teste direto (`geometry.intersectsCoordinate`) percorre TODOS os vértices
 * da área a cada foco: um estado grande com dezenas de milhares de focos na
 * caixa envolvente levava ~100 ms (medido com dados reais). Aqui as arestas
 * são distribuídas UMA vez em faixas horizontais; o raio de um ponto só cruza
 * arestas da faixa em que ele está, então cada teste olha poucas dezenas de
 * arestas em vez de milhares.
 *
 * Regra par-ímpar sobre todos os anéis (buracos e multipolígonos sem
 * sobreposição, como nos dados do projeto).
 */

export interface AreaShape {
  /** Caixa envolvente [minX, minY, maxX, maxY]. */
  extent: readonly [number, number, number, number];
  contains: (x: number, y: number) => boolean;
}

/**
 * Monta o teste a partir de coordenadas planas XY e do fim de cada anel
 * (índice exclusivo em `flat`, como `Polygon.getEnds()` do OpenLayers).
 */
export function buildAreaShape(flat: ArrayLike<number>, ringEnds: readonly number[]): AreaShape {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const end = ringEnds.length ? ringEnds[ringEnds.length - 1]! : 0;
  for (let i = 0; i + 1 < end; i += 2) {
    const x = flat[i]!;
    const y = flat[i + 1]!;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  if (!(minX <= maxX && minY <= maxY)) {
    return { extent: [0, 0, -1, -1], contains: () => false };
  }

  // Arestas (x1, y1, x2, y2), incluindo o fechamento de cada anel.
  const edges: number[] = [];
  let start = 0;
  for (const ringEnd of ringEnds) {
    const n = (ringEnd - start) >> 1;
    for (let k = 0; k < n; k++) {
      const a = start + 2 * k;
      const b = k + 1 < n ? a + 2 : start;
      const y1 = flat[a + 1]!;
      const y2 = flat[b + 1]!;
      if (y1 === y2) continue; // horizontal: nunca é cruzada pelo raio
      edges.push(flat[a]!, y1, flat[b]!, y2);
    }
    start = ringEnd;
  }
  const edgeCount = edges.length / 4;

  // Nº de faixas ~ 2·√arestas: poucas arestas por faixa sem gastar memória à toa.
  const bands = Math.max(1, Math.min(4096, Math.ceil(2 * Math.sqrt(edgeCount))));
  const height = (maxY - minY) / bands || 1;
  const bandOf = (y: number) => Math.max(0, Math.min(bands - 1, Math.floor((y - minY) / height)));

  // CSR: contagem por faixa, depois preenchimento.
  const offsets = new Uint32Array(bands + 1);
  for (let e = 0; e < edgeCount; e++) {
    const y1 = edges[4 * e + 1]!;
    const y2 = edges[4 * e + 3]!;
    const b1 = bandOf(Math.min(y1, y2));
    const b2 = bandOf(Math.max(y1, y2));
    for (let b = b1; b <= b2; b++) offsets[b + 1]!++;
  }
  for (let b = 0; b < bands; b++) offsets[b + 1]! += offsets[b]!;
  const cursor = offsets.slice(0, bands);
  const banded = new Float64Array(offsets[bands]! * 4);
  for (let e = 0; e < edgeCount; e++) {
    const y1 = edges[4 * e + 1]!;
    const y2 = edges[4 * e + 3]!;
    const b1 = bandOf(Math.min(y1, y2));
    const b2 = bandOf(Math.max(y1, y2));
    for (let b = b1; b <= b2; b++) {
      const o = 4 * cursor[b]!++;
      banded[o] = edges[4 * e]!;
      banded[o + 1] = y1;
      banded[o + 2] = edges[4 * e + 2]!;
      banded[o + 3] = y2;
    }
  }

  return {
    extent: [minX, minY, maxX, maxY],
    contains: (x, y) => {
      if (!(x >= minX && x <= maxX && y >= minY && y <= maxY)) return false;
      const b = bandOf(y);
      let inside = false;
      for (let k = offsets[b]!, last = offsets[b + 1]!; k < last; k++) {
        const o = 4 * k;
        const y1 = banded[o + 1]!;
        const y2 = banded[o + 3]!;
        if (y1 > y !== y2 > y) {
          const x1 = banded[o]!;
          if (x < ((banded[o + 2]! - x1) * (y - y1)) / (y2 - y1) + x1) inside = !inside;
        }
      }
      return inside;
    },
  };
}
